import {
  collection,
  addDoc,
  getDocs,
  query,
  where,
  orderBy,
  limit,
  startAfter,
  Timestamp,
  deleteDoc,
  doc,
  QueryDocumentSnapshot,
  DocumentData
} from 'firebase/firestore';
import { db } from '../utils/firebaseConfig';
import type {
  Activity,
  CreateActivityData,
  ActivityFilters,
  ActivityType
} from '../types/activity';

interface PaginationOptions {
  limit?: number;
  lastDoc?: QueryDocumentSnapshot<DocumentData>;
}

interface ActivitiesResult {
  activities: Activity[];
  lastDoc?: QueryDocumentSnapshot<DocumentData>;
  hasMore: boolean;
}

class ActivityService {
  private readonly COLLECTION_NAME = 'activities';

  /**
   * Remove undefined values from an object (recursively for nested objects)
   */
  private removeUndefined(obj: Record<string, any>): Record<string, any> {
    return Object.fromEntries(
      Object.entries(obj)
        .filter(([_, value]) => value !== undefined)
        .map(([key, value]) => [
          key,
          value && typeof value === 'object' && !Array.isArray(value) && !(value instanceof Timestamp)
            ? this.removeUndefined(value)
            : value
        ])
    );
  }

  /**
   * Create a new activity
   */
  async createActivity(data: CreateActivityData): Promise<string> {
    try {
      const now = Timestamp.now();

      // Remove undefined values as Firestore doesn't accept them
      const cleanData = this.removeUndefined(data as Record<string, any>);

      const docRef = await addDoc(collection(db, this.COLLECTION_NAME), {
        ...cleanData,
        createdAt: now
      });

      return docRef.id;
    } catch (error) {
      console.error('Error creating activity:', error);
      throw new Error('Failed to create activity');
    }
  }

  /**
   * Get activities from a list of users (for feed)
   */
  async getActivitiesFromUsers(
    userIds: string[],
    filters?: ActivityFilters,
    pagination?: PaginationOptions
  ): Promise<ActivitiesResult> {
    try {
      if (userIds.length === 0) {
        return { activities: [], hasMore: false };
      }

      const pageLimit = pagination?.limit || 20;

      // Build query
      let q = query(
        collection(db, this.COLLECTION_NAME),
        where('userId', 'in', userIds.slice(0, 10)), // Firestore 'in' limit is 10
        orderBy('createdAt', 'desc'),
        limit(pageLimit + 1)
      );

      // Add category filter if specified
      if (filters?.category) {
        q = query(
          collection(db, this.COLLECTION_NAME),
          where('userId', 'in', userIds.slice(0, 10)),
          where('eventCategory', '==', filters.category),
          orderBy('createdAt', 'desc'),
          limit(pageLimit + 1)
        );
      }

      // Add pagination
      if (pagination?.lastDoc) {
        q = query(q, startAfter(pagination.lastDoc));
      }

      const querySnapshot = await getDocs(q);
      const docs = querySnapshot.docs;

      const hasMore = docs.length > pageLimit;
      const activities = docs.slice(0, pageLimit).map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as Activity[];

      const lastDoc = hasMore ? docs[pageLimit - 1] : undefined;

      return { activities, lastDoc, hasMore };
    } catch (error) {
      console.error('Error fetching activities from users:', error);
      throw new Error('Failed to fetch activities');
    }
  }

  /**
   * Get all activities for a specific event (event timeline)
   */
  async getActivitiesByEvent(
    eventId: string,
    pagination?: PaginationOptions
  ): Promise<ActivitiesResult> {
    try {
      const pageLimit = pagination?.limit || 20;

      let q = query(
        collection(db, this.COLLECTION_NAME),
        where('eventId', '==', eventId),
        orderBy('createdAt', 'desc'),
        limit(pageLimit + 1)
      );

      if (pagination?.lastDoc) {
        q = query(q, startAfter(pagination.lastDoc));
      }

      const querySnapshot = await getDocs(q);
      const docs = querySnapshot.docs;

      const hasMore = docs.length > pageLimit;
      const activities = docs.slice(0, pageLimit).map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as Activity[];

      const lastDoc = hasMore ? docs[pageLimit - 1] : undefined;

      return { activities, lastDoc, hasMore };
    } catch (error) {
      console.error('Error fetching activities by event:', error);
      throw new Error('Failed to fetch event activities');
    }
  }

  /**
   * Get activities for a specific user (user's activity history)
   */
  async getUserActivities(
    userId: string,
    pagination?: PaginationOptions
  ): Promise<ActivitiesResult> {
    try {
      const pageLimit = pagination?.limit || 20;

      let q = query(
        collection(db, this.COLLECTION_NAME),
        where('userId', '==', userId),
        orderBy('createdAt', 'desc'),
        limit(pageLimit + 1)
      );

      if (pagination?.lastDoc) {
        q = query(q, startAfter(pagination.lastDoc));
      }

      const querySnapshot = await getDocs(q);
      const docs = querySnapshot.docs;

      const hasMore = docs.length > pageLimit;
      const activities = docs.slice(0, pageLimit).map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as Activity[];

      const lastDoc = hasMore ? docs[pageLimit - 1] : undefined;

      return { activities, lastDoc, hasMore };
    } catch (error) {
      console.error('Error fetching user activities:', error);
      throw new Error('Failed to fetch user activities');
    }
  }

  /**
   * Delete all activities for an event (cleanup when event is deleted)
   */
  async deleteActivitiesByEvent(eventId: string): Promise<void> {
    try {
      const q = query(
        collection(db, this.COLLECTION_NAME),
        where('eventId', '==', eventId)
      );

      const querySnapshot = await getDocs(q);
      const deletePromises = querySnapshot.docs.map(docSnapshot =>
        deleteDoc(doc(db, this.COLLECTION_NAME, docSnapshot.id))
      );

      await Promise.all(deletePromises);
    } catch (error) {
      console.error('Error deleting activities by event:', error);
      throw new Error('Failed to delete event activities');
    }
  }

  /**
   * Delete a specific activity
   */
  async deleteActivity(activityId: string): Promise<void> {
    try {
      await deleteDoc(doc(db, this.COLLECTION_NAME, activityId));
    } catch (error) {
      console.error('Error deleting activity:', error);
      throw new Error('Failed to delete activity');
    }
  }
}

export const activityService = new ActivityService();
